import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import jsQR from "jsqr";
import { ArrowLeft, Camera, CheckCircle, XCircle, Upload, Lock } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { reconstructFinderTopRight } from "../lib/vaultQr";

/* ────────────────────────────────────────────────────────────────────
   /vault/scan — Curb Crew's proprietary Vault QR reader.

   Two input modes:
     - Live camera (continuous frames → reconstruct → jsQR → verify)
     - Image upload (drag/select a PNG of the Vault QR)

   For each frame we:
     1. Try plain jsQR first (in case the QR is intact).
     2. If that fails, run reconstructFinderTopRight() and retry.
     3. On a successful decode, POST the token to /public/vault-qr/scan
        for HMAC + expiry verification. On success, route to the
        cardholder's public profile.

   The whole point of this page: a regular phone camera cannot decode
   a Vault QR, but our reconstruction step makes it decode here.
   ──────────────────────────────────────────────────────────────────── */

export default function VaultQrScan() {
  const [mode, setMode] = useState("camera"); // 'camera' | 'upload'
  const [status, setStatus] = useState("idle"); // idle | scanning | decoded | error
  const [message, setMessage] = useState("");
  const [result, setResult] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const canvasRef = useRef(null); // hidden — reused for each frame
  const scanLoopRef = useRef(null);
  const verifyingRef = useRef(false);
  const navigate = useNavigate();

  // ─── Helper: process a single ImageData frame, return decoded token or null ──
  function tryDecode(imageData) {
    // First try plain — handles intact QRs and lets us short-circuit.
    let decoded = jsQR(imageData.data, imageData.width, imageData.height, {
      inversionAttempts: "dontInvert",
    });
    if (decoded?.data) return { data: decoded.data, reconstructed: false };

    // Second pass: reconstruct missing finder, decode again.
    const repaired = reconstructFinderTopRight(imageData);
    if (!repaired) return null;
    decoded = jsQR(repaired.data, repaired.width, repaired.height, {
      inversionAttempts: "dontInvert",
    });
    if (decoded?.data) return { data: decoded.data, reconstructed: true };
    return null;
  }

  async function verifyToken(token, reconstructed) {
    if (verifyingRef.current) return;
    verifyingRef.current = true;
    setStatus("decoded");
    setMessage(reconstructed ? "Decoded with finder reconstruction. Verifying signature..." : "Decoded (intact QR). Verifying signature...");
    try {
      const r = await api.post("/public/vault-qr/scan", { token });
      setResult({ ...r.data, reconstructed });
      setMessage("Verified.");
      stopCamera();
    } catch (e) {
      setStatus("error");
      setMessage(formatApiError(e));
      verifyingRef.current = false;
    }
  }

  // ─── Token can come from the QR payload directly OR from an embedded URL ──
  function extractToken(decoded) {
    if (!decoded) return "";
    // If it's a full scan URL like https://x/vault/scan#TOKEN we strip the hash.
    try {
      if (decoded.startsWith("http")) {
        const u = new URL(decoded);
        if (u.hash) return decodeURIComponent(u.hash.slice(1));
      }
    } catch { /* not a URL */ }
    return decoded;
  }

  // ─── Camera loop ──
  async function startCamera() {
    setMessage("");
    setResult(null);
    verifyingRef.current = false;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setStatus("scanning");
      setMessage("Point at a Vault QR. Standard scanners will fail — this one knows the trick.");
      scanLoopRef.current = requestAnimationFrame(scanFrame);
    } catch (e) {
      setStatus("error");
      setMessage(`Camera unavailable: ${e.message}. Try the upload option below.`);
    }
  }

  function stopCamera() {
    if (scanLoopRef.current) {
      cancelAnimationFrame(scanLoopRef.current);
      scanLoopRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }

  function scanFrame() {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState !== 4 || verifyingRef.current) {
      scanLoopRef.current = requestAnimationFrame(scanFrame);
      return;
    }
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

    const hit = tryDecode(imageData);
    if (hit) {
      const tok = extractToken(hit.data);
      verifyToken(tok, hit.reconstructed);
      return;
    }
    scanLoopRef.current = requestAnimationFrame(scanFrame);
  }

  // ─── Upload mode ──
  function onFile(ev) {
    const file = ev.target.files?.[0];
    if (!file) return;
    setMessage("Decoding uploaded image...");
    setStatus("scanning");
    setResult(null);
    verifyingRef.current = false;

    const img = new Image();
    img.onload = () => {
      const canvas = canvasRef.current;
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, img.width, img.height);
      const hit = tryDecode(imageData);
      if (hit) {
        const tok = extractToken(hit.data);
        verifyToken(tok, hit.reconstructed);
      } else {
        setStatus("error");
        setMessage("No QR detected in image (even after reconstruction).");
      }
    };
    img.onerror = () => {
      setStatus("error");
      setMessage("Could not load image.");
    };
    img.src = URL.createObjectURL(file);
  }

  useEffect(() => () => stopCamera(), []);

  function viewProfile() {
    if (result?.code) navigate(`/a/${result.code}`);
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-neutral-800">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="label-tag inline-flex items-center gap-2 text-[color:var(--cc-yellow)]" data-testid="vault-scan-back">
            <ArrowLeft size={14} weight="bold" /> Home
          </Link>
          <div className="label-tag inline-flex items-center gap-2">
            <Lock size={14} weight="bold" /> CURB VAULT SCANNER
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className="label-tag text-[color:var(--cc-yellow)]">PROPRIETARY SCANNER</div>
        <h1 className="font-display text-3xl sm:text-5xl mt-3 leading-tight">
          Scan a <span className="bg-[color:var(--cc-yellow)] text-black px-1">Vault QR</span>.
        </h1>
        <p className="mt-3 text-sm text-neutral-300 max-w-xl leading-relaxed">
          The QR has one finder pattern missing — a regular phone camera silently fails.
          This page rebuilds the finder in-memory before decoding. Try it on the same image
          your phone camera couldn&apos;t read.
        </p>

        <div className="mt-8 flex gap-3">
          <button
            onClick={() => setMode("camera")}
            className={`label-tag px-3 py-2 border ${mode === "camera" ? "bg-[color:var(--cc-yellow)] text-black border-[color:var(--cc-yellow)]" : "border-neutral-700 text-neutral-300"}`}
            data-testid="vault-mode-camera"
          >
            <Camera size={14} weight="bold" className="inline mr-1" /> CAMERA
          </button>
          <button
            onClick={() => setMode("upload")}
            className={`label-tag px-3 py-2 border ${mode === "upload" ? "bg-[color:var(--cc-yellow)] text-black border-[color:var(--cc-yellow)]" : "border-neutral-700 text-neutral-300"}`}
            data-testid="vault-mode-upload"
          >
            <Upload size={14} weight="bold" className="inline mr-1" /> UPLOAD IMAGE
          </button>
        </div>

        {mode === "camera" && (
          <div className="mt-6 border-2 border-neutral-800 p-4">
            <div className="aspect-square bg-black flex items-center justify-center relative overflow-hidden">
              <video
                ref={videoRef}
                playsInline
                muted
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="vault-scanner-video"
              />
              {status === "idle" && (
                <button
                  onClick={startCamera}
                  className="relative z-10 inline-flex items-center gap-2 bg-[color:var(--cc-yellow)] text-black px-5 py-3 label-tag border border-[color:var(--cc-yellow)]"
                  data-testid="vault-start-camera-btn"
                >
                  <Camera size={14} weight="bold" /> START CAMERA
                </button>
              )}
            </div>
          </div>
        )}

        {mode === "upload" && (
          <div className="mt-6 border-2 border-dashed border-neutral-700 p-12 text-center">
            <input
              type="file"
              accept="image/*"
              onChange={onFile}
              className="text-sm text-neutral-300"
              data-testid="vault-upload-input"
            />
            <p className="mt-3 text-xs text-neutral-500">PNG / JPG with one finder pattern missing.</p>
          </div>
        )}

        {/* hidden canvas reused per frame */}
        <canvas ref={canvasRef} className="hidden" />

        {/* Status pill */}
        {message && (
          <div
            className={`mt-6 p-4 border-2 text-sm flex items-start gap-3 ${
              status === "error"
                ? "border-red-500 bg-red-950"
                : status === "decoded"
                ? "border-[color:var(--cc-yellow)] bg-neutral-900"
                : "border-neutral-700 bg-neutral-900"
            }`}
            data-testid="vault-scan-status"
          >
            {status === "error" ? (
              <XCircle size={20} weight="bold" className="text-red-400 shrink-0" />
            ) : status === "decoded" ? (
              <CheckCircle size={20} weight="bold" className="text-[color:var(--cc-yellow)] shrink-0" />
            ) : null}
            <span className="leading-relaxed">{message}</span>
          </div>
        )}

        {/* Result card */}
        {result && (
          <div className="mt-8 border-2 border-[color:var(--cc-yellow)] p-6 bg-neutral-900">
            <div className="label-tag text-[color:var(--cc-yellow)]">VERIFIED VAULT QR</div>
            <div className="mt-3 grid sm:grid-cols-2 gap-4 text-sm">
              <div>
                <div className="label-tag text-neutral-500">CARDHOLDER CODE</div>
                <div className="font-mono-cc text-xl mt-1">{result.code}</div>
              </div>
              <div>
                <div className="label-tag text-neutral-500">LABEL</div>
                <div className="mt-1">{result.label || <span className="text-neutral-500">—</span>}</div>
              </div>
              <div>
                <div className="label-tag text-neutral-500">SCAN COUNT</div>
                <div className="mt-1 font-mono-cc">{result.scan_count}</div>
              </div>
              <div>
                <div className="label-tag text-neutral-500">DECODE PATH</div>
                <div className="mt-1 font-mono-cc text-xs">
                  {result.reconstructed ? "RECONSTRUCTED (vault QR)" : "INTACT (standard QR)"}
                </div>
              </div>
            </div>
            <button
              onClick={viewProfile}
              className="mt-6 inline-flex items-center gap-2 bg-[color:var(--cc-yellow)] text-black px-5 py-3 label-tag"
              data-testid="vault-view-profile-btn"
            >
              Open cardholder profile →
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
